import { AlertCircle, Loader2, Sparkles } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Progress } from '@/components/ui/progress'
import { useBookAiStore } from '@/store/useBookAiStore'

import type { BookAiReaderState } from './useBookAi'

/** 解析の開始・進捗・失敗の表示（解析が済んでいれば何も出さない） */
export const AnalysisPanel = ({ bookAi }: { bookAi: BookAiReaderState }) => {
  const startAnalysis = useBookAiStore((state) => state.startAnalysis)
  const isStatusLoading = useBookAiStore((state) => state.isStatusLoading)
  const { status, sections, isJobActive } = bookAi
  const job = status?.job ?? null

  if (!status) {
    return (
      <div className="flex justify-center py-6">
        {isStatusLoading ? <Loader2 className="size-5 animate-spin text-muted-foreground" /> : null}
      </div>
    )
  }

  if (isJobActive && job) {
    return (
      <div className="space-y-2 rounded-xl border p-3">
        <div className="flex items-center gap-2 text-sm">
          <Loader2 className="size-4 shrink-0 animate-spin text-primary" />
          <span className="min-w-0 flex-1 truncate">{job.status === 'pending' ? '順番待ち' : job.message}</span>
          <span className="shrink-0 text-xs text-muted-foreground">{job.progress}%</span>
        </div>
        <Progress value={job.progress} />
        <p className="text-xs text-muted-foreground">
          解析の済んだ章から順に使えます。リーダーを閉じても解析は続きます
        </p>
      </div>
    )
  }

  const analyzed = sections.filter((s) => s.analysis).length
  const isComplete = sections.length > 0 && analyzed === sections.length && !!status.thesis && status.chat_ready
  if (isComplete && status.outdated_sections > 0 && job?.status !== 'failed') {
    return (
      <div className="space-y-2 rounded-xl border p-3">
        <p className="text-sm text-muted-foreground">
          {status.outdated_sections} 章が古い形式の解析です。解析し直すと、重要文に重要度（核心・重要・補足）が付きます
        </p>
        <Button size="sm" variant="outline" onClick={startAnalysis} className="w-full">
          最新の解析に更新
        </Button>
      </div>
    )
  }
  if (isComplete) return null

  if (job?.status === 'failed') {
    return (
      <div className="space-y-2 rounded-xl border border-destructive/40 p-3">
        <p className="flex items-start gap-2 text-sm text-destructive">
          <AlertCircle className="mt-0.5 size-4 shrink-0" />
          <span className="wrap-break-word">{job.error || job.message}</span>
        </p>
        <Button size="sm" variant="outline" onClick={startAnalysis} className="w-full">
          続きを解析
        </Button>
      </div>
    )
  }

  if (analyzed > 0) {
    return (
      <div className="space-y-2 rounded-xl border p-3">
        <p className="text-sm text-muted-foreground">
          {analyzed} / {sections.length} 章を解析済みです
        </p>
        <Button size="sm" variant="outline" onClick={startAnalysis} className="w-full">
          続きを解析
        </Button>
      </div>
    )
  }

  return (
    <div className="space-y-3 rounded-xl border bg-card p-4 text-center">
      <Sparkles className="mx-auto size-6 text-amber-500" />
      <div className="space-y-1 text-sm text-muted-foreground">
        <p>AI がこの本を章ごとに読み、次のものを用意します</p>
        <ul className="mx-auto w-fit list-disc pl-5 text-left text-xs">
          <li>章の要約・読む前の問い・キーワード</li>
          <li>流し読み用の重要文の強調</li>
          <li>登場人物・用語集</li>
          <li>本全体の主張と、本と対話するチャット</li>
        </ul>
        <p className="text-xs">本の長さによって数分〜十数分かかります</p>
      </div>
      <Button onClick={startAnalysis} className="w-full">
        <Sparkles className="size-4" />
        AI で解析する
      </Button>
    </div>
  )
}
